import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { ChoiceGroup } from 'uikit';

import { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import type { IBimLevelChoiceProps } from './types';
import { getItemPriceLevelLabel, priceLevels } from './types';

const saveObjKey = 'priceLevel';

export const BimPriceLevelChoice = ({
    style,
    priceLevel,
    setPriceLevel,
    isDisabledWithoutValue,
}: IBimLevelChoiceProps) => {
    const { id: projectId } = useParams<{ id: string }>();

    const { handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                customSaveKey: `${projectId}_priceLevel`,
                filterSetter: ({ savedValuesObj }) => {
                    setPriceLevel(savedValuesObj?.[saveObjKey]);
                },
                withEmptyFilterSetter: (filterSaver) => {
                    setPriceLevel(priceLevels[0].id);

                    filterSaver({
                        key: 'priceLevel',
                        newState: priceLevels[0].id,
                    });
                },
            }),
            [projectId, setPriceLevel],
        ),
    );

    const choiceValue = useMemo(
        () => (isDisabledWithoutValue ? undefined : priceLevels.find((c) => c.id === priceLevel)),
        [isDisabledWithoutValue, priceLevel],
    );

    const onChoiceChange = useCallback(
        ({ value }) => {
            handleSaveFilterState({
                key: 'priceLevel',
                newState: value?.id,
            });
            setPriceLevel(value?.id);
        },
        [handleSaveFilterState, setPriceLevel],
    );

    return (
        <ChoiceGroup
            value={choiceValue}
            onChange={onChoiceChange}
            items={priceLevels}
            getItemLabel={getItemPriceLevelLabel}
            view="ghost"
            multiple={false}
            name="bimLevelChoice"
            size="s"
            style={style ?? { marginLeft: 'auto' }}
            disabled={isDisabledWithoutValue}
        />
    );
};

BimPriceLevelChoice.displayName = 'BimPriceLevelChoice';
